import React, {
    createContext,
    useContext,
    useMemo,
    ComponentType,
    HTMLAttributes,
    ReactNode
} from 'react';
import { getDocumentBody } from '../../utils/browser.utils';
import { useLocale } from '@honeybook/i18n-react';
import hbuiI18n from '../../i18n';
import type {
    HBLinkTarget,
    HBResolvedLink
} from '../../hooks/useLinkBehavior/useLinkBehavior.types';

interface HBUIContext {
    /** Returns the DOM element used as the portal target for corner popups and tooltips; defaults to document.body. */
    getCornerPopupPortalTarget?: () => HTMLElement | null;
    /** When true, HBText shows a tooltip on hover when content is truncated with ellipsis. HBTimezoneInput and HBPhoneInput read this too and forward it to HBCombobox's `showOverflowTooltip` prop for the same behavior on their trigger text. */
    isHintedEllipsis?: boolean;
    /**
     * Custom routing link component (e.g. React Router's Link) used by HBLink, HBTabs, and other navigation components instead of native anchor tags.
     * It must forward `onClick`, `role`, `aria-disabled`, `target`, `rel`, `download` and `data-routed` to the element it renders, or click handling, the disabled state and `LinkDriver` break.
     * It receives `resolveLink`'s `to` when the resolver returns one, and the original `to` otherwise. The original can be a route object (`{ route, params }`), which a path-only component such as React Router's `Link` cannot take, so return a router-relative `to` from `resolveLink` for those.
     */
    LinkComponent?: ComponentType<{ to: any } & HTMLAttributes<HTMLElement>>;
    /** Classifies a link target as in-app (rendered through `LinkComponent`) or external (a plain anchor) and returns the URL it resolves to. Omit it to use the default: route objects and plain paths are in-app, strings with a scheme or a leading `//` are external. Provide it when the app can build real URLs for route objects, or has same-origin paths that are not router routes. Return `to` as well when `LinkComponent` cannot take the original target: it must be router-relative, without the router's basename. `defaultResolveLink` is exported for resolvers that only handle some targets. */
    resolveLink?: (to: HBLinkTarget) => HBResolvedLink;
}

globalThis.HBUIContext ??= createContext<HBUIContext>({
    getCornerPopupPortalTarget: getDocumentBody,
    isHintedEllipsis: false,
    LinkComponent: undefined,
    resolveLink: undefined
});

const context = globalThis.HBUIContext as React.Context<HBUIContext>;

interface HBUIProviderProps {
    value: HBUIContext;
    children: ReactNode;
}

function HBUIProvider({ value, children }: HBUIProviderProps) {
    return <context.Provider value={value}>{children}</context.Provider>;
}

export const useHBUI = () => useContext(context);

/**
 * Returns a `t()` function fixed to the current locale from LocalizationProvider.
 * Re-renders (and returns a new `t`) whenever the locale changes.
 * Use this instead of importing `t` directly from `../../i18n`.
 */
export function useHBTranslation() {
    const { locale } = useLocale();
    return useMemo(() => hbuiI18n.getFixedT(locale, 'hbui'), [locale]);
}

export default HBUIProvider;
